/**
 * Signup popup — trigger, frequency, two-step, minimise-to-tab, success handling.
 *
 * Triggers (data-trigger): timed | exit_intent | both.
 * Gates: min page views this session; per-popup dismissal window; permanent
 *        "signed up" suppression.
 * Two-step (data-two-step): offer + button first, fields on the second click.
 *
 * Minimise (data-minimise="true"): instead of closing, the popup collapses to a
 * small left-edge tab that re-opens the offer on click. The × / backdrop / Esc
 * minimise; the "No thanks" link declines for good. The tab persists (site-wide,
 * for the suppress window) as a gentle re-entry point, is removed once the
 * shopper signs up, and is stashed whenever a cart drawer / menu / modal opens
 * so it never sits over off-canvas UI.
 *
 * Success: {% form 'customer' %} posts and reloads; a sessionStorage "pending"
 * flag lets us tell our submission apart from other customer forms on the page.
 */
(function () {
  'use strict';

  var root = document.getElementById('signup-popup');
  if (!root) return;

  var tab = document.getElementById('sgp-tab');

  var cfg = {
    key: root.getAttribute('data-key') || 'default',
    trigger: root.getAttribute('data-trigger') || 'timed',
    delay: (parseInt(root.getAttribute('data-delay'), 10) || 0) * 1000,
    minPageviews: parseInt(root.getAttribute('data-min-pageviews'), 10) || 1,
    suppressDays: parseInt(root.getAttribute('data-suppress-days'), 10) || 14,
    slideInterval: (parseInt(root.getAttribute('data-slide-interval'), 10) || 4) * 1000,
    twoStep: root.getAttribute('data-two-step') === 'true',
    minimise: root.getAttribute('data-minimise') === 'true',
    scroll: parseInt(root.getAttribute('data-scroll'), 10) || 0,
    deferOffer: root.getAttribute('data-defer-offer') === 'true',
    variant: root.getAttribute('data-variant') || '',
    ab: root.getAttribute('data-ab') || ''
  };

  // Skip autofocusing the field on touch/small screens — it yanks up the
  // keyboard and can cover the popup. Focus a control instead.
  var coarse = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || window.innerWidth <= 600;

  var SIGNED = 'sgp_signed';                 // permanent + global
  var DISMISS = 'sgp_dismissed_' + cfg.key;  // per-popup dismissal window
  var MIN = 'sgp_minimised_' + cfg.key;      // per-popup: collapsed-to-tab since
  var PENDING = 'sgp_pending';               // sessionStorage: our form mid-submit
  var PVIEWS = 'sgp_pageviews';              // sessionStorage: pages seen

  function ls(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function setLs(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function clrLs(k) { try { localStorage.removeItem(k); } catch (e) {} }
  function ss(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function setSs(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }
  function clrSs(k) { try { sessionStorage.removeItem(k); } catch (e) {} }

  function withinWindow(k) {
    var t = ls(k);
    return t && (Date.now() - parseInt(t, 10)) < cfg.suppressDays * 86400000;
  }
  function signedUp() { return ls(SIGNED) === '1'; }
  function suppressed() { return withinWindow(DISMISS); }
  function minimised() { return cfg.minimise && withinWindow(MIN); }

  // Count this page view once per load (drives the min-page-views gate).
  var pageViews = (parseInt(ss(PVIEWS), 10) || 0) + 1;
  setSs(PVIEWS, String(pageViews));

  function emit(type, payload) {
    var d = {}, k;
    payload = payload || {};
    for (k in payload) { if (Object.prototype.hasOwnProperty.call(payload, k)) d[k] = payload[k]; }
    // Stamp the A/B variant on every event so conversion can be sliced per
    // variant in the Supabase pipeline (site-metrics forwards payload as-is).
    if (cfg.ab) { d.variant = cfg.variant; d.ab_test = cfg.ab; }
    try { window.dispatchEvent(new CustomEvent('ws:track', { detail: { type: type, payload: d } })); } catch (e) {}
  }

  // ---- views + steps --------------------------------------------------------
  var formView = root.querySelector('[data-sgp-view="form"]');
  var successView = root.querySelector('[data-sgp-view="success"]');
  var step1 = root.querySelector('[data-sgp-step="1"]');
  var step2 = root.querySelector('[data-sgp-step="2"]');

  function showView(name) {
    if (formView) formView.hidden = name !== 'form';
    if (successView) successView.hidden = name !== 'success';
  }
  function gotoStep(n) {
    if (!cfg.twoStep || !step1 || !step2) return;
    step1.hidden = n !== 1;
    step2.hidden = n !== 2;
    if (n === 2) {
      var f = step2.querySelector('.sgp__input');
      if (f) { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } }
    }
  }

  var nextBtn = root.querySelector('[data-sgp-next]');
  if (nextBtn) nextBtn.addEventListener('click', function () { gotoStep(2); emit('signup_popup_step2', {}); });

  // ---- tab (minimised state) ------------------------------------------------
  function showTab() {
    if (!tab) return;
    tab.hidden = false;
    requestAnimationFrame(function () { tab.classList.add('is-in'); });
  }
  function hideTab() {
    if (!tab) return;
    tab.classList.remove('is-in');
    var done = function () { tab.hidden = true; tab.removeEventListener('transitionend', done); };
    tab.addEventListener('transitionend', done);
    setTimeout(done, 320);
  }
  function stashTab(on) { if (tab) tab.classList.toggle('is-stash', on); }
  function tabVisible() { return tab && !tab.hidden; }

  if (tab) {
    tab.addEventListener('click', function () {
      hideTab();
      open('form', 1);
      emit('signup_popup_reopened', {});
    });
  }

  // ---- open / hide overlay --------------------------------------------------
  var opened = false;

  function open(view, step) {
    if (opened) return;
    opened = true;
    hideTab();
    showView(view || 'form');
    if (cfg.twoStep && view !== 'success') gotoStep(step || 1);
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function () { root.classList.add('is-open'); });
    document.addEventListener('keydown', onKey);
    startSlides();
    var sel = (view === 'success')
      ? '.sgp__code, .sgp__submit--link, .sgp__close'
      : (coarse ? '.sgp__close' : '.sgp__input, [data-sgp-next], .sgp__close');
    var focusTarget = root.querySelector(sel);
    if (focusTarget) { try { focusTarget.focus({ preventScroll: true }); } catch (e) { focusTarget.focus(); } }
    emit('signup_popup_shown', { view: view || 'form', two_step: cfg.twoStep });
  }

  function hideOverlay() {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', onKey);
    stopSlides();
    var done = function () { root.hidden = true; root.removeEventListener('transitionend', done); };
    root.addEventListener('transitionend', done);
    setTimeout(done, 400);
    opened = false;
  }

  // Soft close → collapse to the re-entry tab (no suppression).
  function minimise() {
    hideOverlay();
    setLs(MIN, String(Date.now()));
    showTab();
    emit('signup_popup_minimised', {});
  }

  // Hard close → suppress for the window and remove the tab.
  function decline() {
    hideOverlay();
    hideTab();
    clrLs(MIN);
    if (!signedUp()) setLs(DISMISS, String(Date.now()));
    emit('signup_popup_dismissed', {});
  }

  // The × / backdrop / Esc: minimise when enabled, otherwise a plain dismissal.
  function softClose() {
    if (cfg.minimise && !signedUp()) minimise();
    else decline();
  }

  // Keep keyboard focus inside the open dialog (accessibility + fewer drop-offs).
  function focusables() {
    return Array.prototype.filter.call(
      root.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null && !el.closest('[hidden]'); }
    );
  }
  function onKey(e) {
    if (e.key === 'Escape') { softClose(); return; }
    if (e.key === 'Tab') {
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  Array.prototype.forEach.call(root.querySelectorAll('[data-sgp-close]'), function (el) {
    el.addEventListener('click', softClose);
  });
  Array.prototype.forEach.call(root.querySelectorAll('[data-sgp-decline]'), function (el) {
    el.addEventListener('click', decline);
  });

  // ---- slideshow ------------------------------------------------------------
  var slidesWrap = root.querySelector('[data-sgp-slides]');
  var slides = slidesWrap ? slidesWrap.querySelectorAll('[data-sgp-slide]') : [];
  var dots = slidesWrap ? slidesWrap.querySelectorAll('[data-sgp-dot]') : [];
  var slideIdx = 0;
  var slideTimer = null;

  function goSlide(i) {
    if (!slides.length) return;
    slideIdx = (i + slides.length) % slides.length;
    Array.prototype.forEach.call(slides, function (s, n) { s.classList.toggle('is-active', n === slideIdx); });
    Array.prototype.forEach.call(dots, function (d, n) { d.classList.toggle('is-active', n === slideIdx); });
  }
  Array.prototype.forEach.call(dots, function (d) {
    d.addEventListener('click', function () { goSlide(parseInt(d.getAttribute('data-sgp-dot'), 10) || 0); restartSlides(); });
  });
  function startSlides() {
    if (slides.length < 2 || slideTimer) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    slideTimer = setInterval(function () { goSlide(slideIdx + 1); }, cfg.slideInterval);
  }
  function stopSlides() { if (slideTimer) { clearInterval(slideTimer); slideTimer = null; } }
  function restartSlides() { stopSlides(); startSlides(); }

  // ---- copy code ------------------------------------------------------------
  var codeBtn = root.querySelector('[data-sgp-code]');
  if (codeBtn) {
    codeBtn.addEventListener('click', function () {
      var code = codeBtn.getAttribute('data-code') || '';
      var label = codeBtn.querySelector('[data-sgp-copy-label]');
      var done = function () { if (label) { var o = label.textContent; label.textContent = 'Copied!'; setTimeout(function () { label.textContent = o; }, 1800); } };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(function () {});
      } else {
        try {
          var t = document.createElement('textarea');
          t.value = code; document.body.appendChild(t); t.select();
          document.execCommand('copy'); document.body.removeChild(t); done();
        } catch (e) {}
      }
      emit('signup_popup_code_copied', { code: code });
    });
  }

  // ---- submit: flag the pending POST ---------------------------------------
  var form = root.querySelector('.sgp__form');
  if (form) form.addEventListener('submit', function () { setSs(PENDING, '1'); });

  // Keep the tab clear of cart drawers / menus / modals (belt-and-suspenders
  // alongside its below-offcanvas z-index).
  if (cfg.minimise) {
    document.addEventListener('show.bs.offcanvas', function () { stashTab(true); });
    document.addEventListener('show.bs.modal', function () { stashTab(true); });
    document.addEventListener('hidden.bs.offcanvas', function () { if (tabVisible()) stashTab(false); });
    document.addEventListener('hidden.bs.modal', function () { if (tabVisible()) stashTab(false); });
  }

  // ---- post-reload success/error handling ----------------------------------
  var state = root.querySelector('[data-sgp-state]');
  var wasPending = ss(PENDING) === '1';
  if (wasPending) {
    clrSs(PENDING);
    var posted = state && state.getAttribute('data-posted') === 'true';
    var hasErrors = state && state.getAttribute('data-has-errors') === 'true';
    if (posted) {
      setLs(SIGNED, '1');
      clrLs(MIN);
      hideTab();
      open('success');
      emit('signup_popup_converted', {});
      return;
    }
    if (hasErrors) {
      // "Email already taken" = an existing customer. Don't scare them with a red
      // error — show the code as a friendly success.
      if (state && state.getAttribute('data-existing') === 'true') {
        setLs(SIGNED, '1');
        clrLs(MIN);
        hideTab();
        open('success');
        emit('signup_popup_existing', {});
        return;
      }
      open('form', 2); // errors live on the fields (step 2 in two-step mode)
      return;
    }
  }

  // ---- arm triggers ---------------------------------------------------------
  if (signedUp()) return;
  if (minimised()) { showTab(); return; }   // already collapsed → just offer the tab
  if (suppressed()) return;
  // Stand down if a bigger auto-apply ad offer is live this session (offer-landing)
  // — don't undercut it or confuse the shopper with a second, smaller code.
  if (cfg.deferOffer && ss('wig_landing_offer') === '1') return;
  if (pageViews < cfg.minPageviews) return; // wait for a later page this session

  function drawerOpen() { return !!document.querySelector('.offcanvas.show, .modal.show'); }

  function tryOpen() {
    if (opened || signedUp() || suppressed()) return;
    if (drawerOpen()) return;
    open('form', 1);
    cleanupTriggers();
  }

  var timer = null;
  var cleanupFns = [];
  function cleanupTriggers() {
    if (timer) { clearTimeout(timer); timer = null; }
    cleanupFns.forEach(function (fn) { fn(); });
    cleanupFns = [];
  }

  if (cfg.trigger === 'timed' || cfg.trigger === 'both') {
    timer = setTimeout(tryOpen, cfg.delay);
  }

  if (cfg.trigger === 'exit_intent' || cfg.trigger === 'both') {
    var onOut = function (e) { if (e.clientY <= 0) tryOpen(); };
    document.addEventListener('mouseout', onOut);
    cleanupFns.push(function () { document.removeEventListener('mouseout', onOut); });

    var lastY = window.pageYOffset;
    var onScroll = function () {
      var y = window.pageYOffset;
      if (y < 140 && lastY - y > 24) tryOpen();
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    cleanupFns.push(function () { window.removeEventListener('scroll', onScroll); });
  }

  // Scroll-depth trigger (composes with the others): open past N% of the page.
  if (cfg.scroll > 0) {
    var onDepth = function () {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      if (h > 0 && (window.pageYOffset / h) * 100 >= cfg.scroll) tryOpen();
    };
    window.addEventListener('scroll', onDepth, { passive: true });
    cleanupFns.push(function () { window.removeEventListener('scroll', onDepth); });
  }
})();
